import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getMatrizTbfin } from "@/lib/api";
import { usePermisos } from "@/lib/authz";

const pad = (n: number) => String(n).padStart(2, "0");

// Estilo de celda por estado.
const CELDA: Record<string, { cls: string; label: string }> = {
  terminado: { cls: "bg-violet-600 text-white", label: "T" },
  aceptado: { cls: "bg-cyan-600 text-white", label: "A" },
  enviado: { cls: "bg-emerald-600 text-white", label: "E" },
  enviando: { cls: "bg-sky-400 text-white animate-pulse", label: "…" },
  confirmado: { cls: "bg-blue-500 text-white", label: "C" },
  sin_activacion: { cls: "bg-slate-400 text-white", label: "S" },
  pendiente: { cls: "bg-amber-100 text-amber-700", label: "P" },
  // TBFin automática ya formalizada por CENACE (detectada en el estado de cuenta).
  formalizada: { cls: "bg-purple-500 text-white", label: "U" },
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export function MatrizPage() {
  const navigate = useNavigate();
  const { has } = usePermisos();
  const puedeAbrir = has("calendario_click");
  // Mes en formato "YYYY-MM". Recuerda la última selección entre navegaciones/recargas
  // (localStorage, mismo patrón que /tbfin); sin selección guardada usa el mes en curso.
  const [ym, setYm] = useState<string>(() => {
    const guardado = localStorage.getItem("matriz.ym");
    if (guardado && /^\d{4}-\d{2}$/.test(guardado)) return guardado;
    const hoy = new Date();
    return `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}`;
  });
  useEffect(() => {
    if (ym) localStorage.setItem("matriz.ym", ym);
  }, [ym]);

  const irATbfin = (idContrato: number, dia: number) => {
    navigate(`/tbfin?contrato=${idContrato}&fecha=${anio}-${pad(mes)}-${pad(dia)}`);
  };

  const [anioStr, mesStr] = ym.split("-");
  const anio = Number(anioStr);
  const mes = Number(mesStr);

  const q = useQuery({
    queryKey: ["matriz", anio, mes],
    queryFn: () => getMatrizTbfin({ anio, mes, modo: "operacion" }),
    enabled: Number.isFinite(anio) && Number.isFinite(mes),
    // Mientras el robot captura en el SIM (celda 'enviando'), refrescar hasta que
    // quede registrada (Enviado REG) o falle y regrese a Confirmado.
    refetchInterval: (query) =>
      query.state.data?.contratos.some((c) => Object.values(c.celdas).includes("enviando"))
        ? 10_000
        : false,
  });

  const data = q.data;
  const dias = data?.dias ?? 0;
  const diasArr = Array.from({ length: dias }, (_, i) => i + 1);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Calendario TBFin</h1>
        <p className="text-sm text-slate-500 mt-1">
          Estado del cálculo por contrato y día — enviado, confirmado o pendiente.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Mes</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={ym}
            onChange={(e) => setYm(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 pb-1">
          <Leyenda cls="bg-amber-100 border border-amber-300" texto="Pendiente" />
          <Leyenda cls="bg-blue-500" texto="Confirmado" />
          <Leyenda cls="bg-slate-400" texto="Sin activación" />
          <Leyenda cls="bg-sky-400" texto="Enviando (capturando en el SIM)" />
          <Leyenda cls="bg-emerald-600" texto="Enviado (REG)" />
          <Leyenda cls="bg-cyan-600" texto="Aceptado (ACE)" />
          <Leyenda cls="bg-violet-600" texto="Terminado (TER)" />
          <Leyenda cls="bg-purple-500" texto="U · Formalizada por CENACE (automática)" />
          <Leyenda cls="bg-white border" texto="No aplica" />
        </div>
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-slate-500">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error).message}
        </div>
      )}

      {data && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <div className="px-4 py-2 border-b text-sm text-slate-600">
            {MESES[mes - 1]} {anio} · {data.contratos.length} contrato(s)
          </div>
          <table className="text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="sticky left-0 bg-slate-100 px-3 py-2 text-left font-medium min-w-[200px] z-10">
                  Contrato
                </th>
                {diasArr.map((d) => (
                  <th key={d} className="px-0 py-2 font-medium text-center w-7">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.contratos.length === 0 && (
                <tr>
                  <td colSpan={dias + 1} className="px-3 py-6 text-center text-slate-500">
                    No hay contratos.
                  </td>
                </tr>
              )}
              {data.contratos.map((c) => (
                <tr key={c.id_contrato} className="border-t">
                  <td className="sticky left-0 bg-white px-3 py-1.5 font-medium text-slate-800 min-w-[200px] z-10">
                    {c.folio}
                  </td>
                  {diasArr.map((d) => {
                    const estado = c.celdas[String(d)];
                    const sty = estado ? CELDA[estado] : null;
                    const base = `w-6 h-6 mx-auto rounded flex items-center justify-center text-[10px] font-semibold ${
                      sty ? sty.cls : "text-slate-300"
                    }`;
                    return (
                      <td key={d} className="p-0.5 text-center">
                        {puedeAbrir ? (
                          <button
                            type="button"
                            onClick={() => irATbfin(c.id_contrato, d)}
                            className={`${base} cursor-pointer hover:ring-2 hover:ring-blue-400 ${
                              sty ? "" : "hover:bg-slate-100"
                            }`}
                            title={
                              (estado ? `Día ${d}: ${estado}` : `Día ${d}: no aplica`) +
                              " — clic para ver el cálculo"
                            }
                          >
                            {sty ? sty.label : "·"}
                          </button>
                        ) : (
                          <span
                            className={`${base} cursor-default`}
                            title={estado ? `Día ${d}: ${estado}` : `Día ${d}: no aplica`}
                          >
                            {sty ? sty.label : "·"}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Leyenda({ cls, texto }: { cls: string; texto: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-3.5 h-3.5 rounded ${cls}`} />
      {texto}
    </span>
  );
}
